import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Umbrella, Stethoscope, Trash2, Filter, X } from 'lucide-react';
import { AUSENCIA_LABEL, AUSENCIA_STYLE } from '../../lib/labels';

const AusenciasTab = () => {
  const [ausencias, setAusencias] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [na, setNa] = useState({ user_id: '', tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' });

  // Filtros
  const [fEmpleado, setFEmpleado] = useState('all');
  const [fTipo, setFTipo] = useState('all');
  const [fDesde, setFDesde] = useState('');
  const [fHasta, setFHasta] = useState('');

  const cargarAusencias = () => api.get('/admin/ausencias').then((r) => setAusencias(r.data)).catch(() => {});
  const cargarEmpleados = () => api.get('/admin/empleados').then((r) => setEmpleados(r.data)).catch(() => {});

  useEffect(() => { cargarAusencias(); cargarEmpleados(); }, []);

  const crearAusencia = async (e) => {
    e.preventDefault();
    if (!na.user_id) { toast.error('Selecciona un empleado'); return; }
    if (na.fecha_fin < na.fecha_inicio) { toast.error('La fecha "Hasta" no puede ser anterior a "Desde"'); return; }
    try {
      await api.post('/admin/ausencias', na);
      toast.success('Ausencia registrada');
      setNa({ user_id: '', tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' });
      cargarAusencias();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const borrarAusencia = async (id) => {
    try { await api.delete(`/admin/ausencias/${id}`); cargarAusencias(); } catch { toast.error('Error'); }
  };

  const limpiarFiltros = () => { setFEmpleado('all'); setFTipo('all'); setFDesde(''); setFHasta(''); };

  const ausenciasFiltradas = useMemo(() => {
    return ausencias.filter((a) => {
      if (fEmpleado !== 'all' && a.user_id !== fEmpleado) return false;
      if (fTipo !== 'all' && a.tipo !== fTipo) return false;
      // Solapamiento con rango [fDesde, fHasta]: la ausencia debe intersectar el rango
      if (fDesde && a.fecha_fin < fDesde) return false;
      if (fHasta && a.fecha_inicio > fHasta) return false;
      return true;
    });
  }, [ausencias, fEmpleado, fTipo, fDesde, fHasta]);

  const filtrosActivos = fEmpleado !== 'all' || fTipo !== 'all' || !!fDesde || !!fHasta;

  return (
    <div className="space-y-6" data-testid="ausencias-tab">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulario nueva ausencia */}
        <Card className="border-zinc-200 h-fit">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2 text-lg">
              <Umbrella className="h-5 w-5" /> Nueva ausencia
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={crearAusencia} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Empleado</Label>
                <Select value={na.user_id} onValueChange={(v) => setNa({ ...na, user_id: v })}>
                  <SelectTrigger data-testid="new-ausencia-empleado"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>
                    {empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={na.tipo} onValueChange={(v) => setNa({ ...na, tipo: v })}>
                  <SelectTrigger data-testid="new-ausencia-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(AUSENCIA_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Desde</Label>
                  <Input type="date" value={na.fecha_inicio} onChange={(e) => setNa({ ...na, fecha_inicio: e.target.value })} required data-testid="new-ausencia-inicio" />
                </div>
                <div className="space-y-1.5">
                  <Label>Hasta</Label>
                  <Input type="date" value={na.fecha_fin} onChange={(e) => setNa({ ...na, fecha_fin: e.target.value })} required data-testid="new-ausencia-fin" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Motivo (opcional)</Label>
                <Input value={na.motivo} onChange={(e) => setNa({ ...na, motivo: e.target.value })} data-testid="new-ausencia-motivo" />
              </div>
              <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800" data-testid="create-ausencia-btn">Registrar ausencia</Button>
            </form>
          </CardContent>
        </Card>

        {/* Listado + filtros */}
        <Card className="border-zinc-200 lg:col-span-2">
          <CardHeader className="space-y-4">
            <CardTitle className="font-heading flex items-center gap-2 text-lg">
              <Stethoscope className="h-5 w-5" />
              Ausencias, vacaciones y bajas
              <Badge variant="outline" className="ml-1" data-testid="ausencias-count">
                {ausenciasFiltradas.length}{filtrosActivos && ausenciasFiltradas.length !== ausencias.length ? ` / ${ausencias.length}` : ''}
              </Badge>
            </CardTitle>

            {/* Filtros */}
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-3" data-testid="ausencias-filtros">
              <div className="flex items-center gap-2 text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                <Filter className="h-3.5 w-3.5" /> Filtros
                {filtrosActivos && (
                  <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={limpiarFiltros} data-testid="ausencias-filtros-limpiar">
                    <X className="h-3 w-3 mr-1" /> Limpiar
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Empleado</Label>
                  <Select value={fEmpleado} onValueChange={setFEmpleado}>
                    <SelectTrigger data-testid="filtro-empleado" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipo</Label>
                  <Select value={fTipo} onValueChange={setFTipo}>
                    <SelectTrigger data-testid="filtro-tipo" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {Object.entries(AUSENCIA_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Desde</Label>
                  <Input type="date" value={fDesde} onChange={(e) => setFDesde(e.target.value)} data-testid="filtro-desde" className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Hasta</Label>
                  <Input type="date" value={fHasta} onChange={(e) => setFHasta(e.target.value)} data-testid="filtro-hasta" className="h-9" />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-[32rem] overflow-y-auto">
              <table className="w-full" data-testid="ausencias-table">
                <thead className="bg-zinc-50 sticky top-0">
                  <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    <th className="py-3 px-5">Empleado</th>
                    <th className="py-3 px-5">Tipo</th>
                    <th className="py-3 px-5">Periodo</th>
                    <th className="py-3 px-5">Motivo</th>
                    <th className="py-3 px-5 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ausenciasFiltradas.map((a) => (
                    <tr key={a.id} className="border-t border-zinc-100" data-testid={`ausencia-row-${a.id}`}>
                      <td className="py-2.5 px-5 font-medium text-zinc-900">{a.full_name}</td>
                      <td className="py-2.5 px-5"><Badge className={AUSENCIA_STYLE[a.tipo]}>{AUSENCIA_LABEL[a.tipo]}</Badge></td>
                      <td className="py-2.5 px-5 tabular-nums text-sm">
                        {new Date(a.fecha_inicio).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })} – {new Date(a.fecha_fin).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })}
                      </td>
                      <td className="py-2.5 px-5 text-sm text-zinc-500">{a.motivo || '-'}</td>
                      <td className="py-2.5 px-5 text-right">
                        <Button size="icon" variant="outline" className="text-red-600" onClick={() => borrarAusencia(a.id)} data-testid={`delete-ausencia-${a.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {ausenciasFiltradas.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-zinc-400 text-sm">
                        {filtrosActivos ? 'Sin resultados con los filtros aplicados.' : 'Sin ausencias registradas.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AusenciasTab;
